import { Link } from 'react-router-dom';
import type { ReactNode } from 'react';
import { cn } from '../../lib/utils';

interface RowPeekNavLinkProps {
  /** React Router target (absolute or query-string path). */
  readonly to: string;
  /** Optional 16x16 SVG / icon node rendered before the label. */
  readonly icon?: ReactNode;
  readonly children: ReactNode;
  /** Additional className(s) to merge into the link's class list. */
  readonly className?: string;
}

/**
 * Single navigation row inside a {@link RowPeekDrawer} body.
 *
 * Renders a `<Link>` with an optional left icon, the label, and a
 * right-pointing arrow that slides slightly on hover. Designed to be
 * stacked vertically inside the drawer body so users get a clean,
 * scannable list of cross-tab jumps from the peek target.
 *
 * Hover affordance: subtle `bg-ice` fill on the row + the arrow
 * translates 2px to the right. Reuses the same `group-hover:translate-x-1`
 * idiom as RankingsRow for visual consistency. Suppressed when the
 * user prefers reduced motion.
 */
export function RowPeekNavLink({ to, icon, children, className }: RowPeekNavLinkProps) {
  return (
    <Link
      to={to}
      className={cn(
        'group flex items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium text-text-1',
        'transition-colors hover:bg-ice focus:outline-none focus:ring-2 focus:ring-blue/40',
        className,
      )}
    >
      {icon && (
        <span aria-hidden="true" className="flex h-4 w-4 shrink-0 items-center justify-center text-blue">
          {icon}
        </span>
      )}
      <span className="flex-1 truncate">{children}</span>
      <svg
        aria-hidden="true"
        width="12"
        height="12"
        viewBox="0 0 12 12"
        fill="none"
        className="shrink-0 text-text-4 transition-transform group-hover:translate-x-0.5 motion-reduce:transition-none motion-reduce:group-hover:translate-x-0"
      >
        <path
          d="M3 1.5L7.5 6L3 10.5"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </Link>
  );
}
